import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/authStore';
import {
  getSession,
  loginPassword,
  loginPasskey,
  registerAccount,
  logoutSession,
} from '@/api/adapter/session';
import { getProfiles, getWorkers } from '@/api/adapter/onboarding';
import type { User, UserRole } from '@/types/user';

export const authKeys = {
  all: ['auth'] as const,
  me: () => ['auth', 'me'] as const,
};

export async function restoreSession(signal?: AbortSignal) {
  const initial = useAuthStore.getState().session;
  try {
    const session = await getSession(signal);
    if (signal?.aborted || useAuthStore.getState().session !== initial) return;
    if (session) useAuthStore.getState().setSession(session);
    else useAuthStore.getState().logout();
  } catch (error) {
    if (!signal?.aborted && useAuthStore.getState().session === initial) {
      useAuthStore
        .getState()
        .setSessionError(
          error instanceof Error ? error.message : 'Cannot verify your session.'
        );
    }
  }
}
export function useAuthBootstrap() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const controller = new AbortController();
    void restoreSession(controller.signal);
    const recheck = () => {
      void restoreSession(controller.signal);
    };
    window.addEventListener('focus', recheck);
    const unsubscribe = useAuthStore.subscribe((state, previous) => {
      if (state.session?.principalId !== previous.session?.principalId) {
        void queryClient.cancelQueries();
        queryClient.clear();
      }
    });
    return () => {
      controller.abort();
      unsubscribe();
      window.removeEventListener('focus', recheck);
    };
  }, [queryClient]);
}
export function useCurrentUser(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      await restoreSession();
      const state = useAuthStore.getState();
      if (!state.user || state.status !== 'authenticated')
        throw new Error(state.sessionError ?? 'Login required.');
      return state.user;
    },
    enabled: options?.enabled ?? false,
    retry: false,
  });
}
export function useLogin() {
  return useMutation({
    gcTime: 0,
    mutationFn: async ({
      username,
      password,
      role,
    }: {
      username: string;
      password?: string;
      role: UserRole;
    }) => {
      const session =
        password === undefined
          ? await loginPasskey(username)
          : await loginPassword(username, password);
      useAuthStore.getState().setSession(session, role, username.toLowerCase());
      return session;
    },
  });
}
export function useRegister() {
  return useMutation({
    gcTime: 0,
    mutationFn: async ({
      username,
      password,
      name,
      role,
    }: {
      username: string;
      password: string;
      name: string;
      role: UserRole;
    }) => {
      const session = await registerAccount(username, password, name);
      useAuthStore.getState().setSession(session, role, username.toLowerCase());
      return session;
    },
  });
}
export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      await logoutSession();
      useAuthStore.getState().logout();
    },
  });
}
export function getUserRole(user: User | null): UserRole | null {
  return (
    user?.role ??
    (user?.clientId ? 'client' : user?.developerId ? 'developer' : null)
  );
}
export async function loginDestination(role: UserRole) {
  const session = useAuthStore.getState().session;
  if (!session) return '/login';
  const profiles = await getProfiles();
  if (useAuthStore.getState().session?.principalId !== session.principalId)
    return '/login';
  useAuthStore.getState().setProfiles(profiles);
  if (role === 'client')
    return profiles.client ? '/dashboard' : '/register/client';
  const workers = await getWorkers();
  return profiles.worker &&
    workers.some((worker) => worker.account === session.accountId)
    ? '/dashboard'
    : '/register/developer';
}
